// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Graph-first Trends workspace: turns the existing trend rows into the
// owner-facing visual summary that sits above the detailed sample flights and
// evidence table.

import { trendVisualAvailable } from "../trends/trend-availability.js";
import { maintenanceContextTrendAnchors } from "../core/maintenance-context.js";
import { formatCompactElapsedTime } from "../core/format.js";
import { createChtHeatProfile } from "./cht-heat-profile.js";
import { createChtDistributionLanes } from "./cht-distribution-lanes.js";
import { trendComparisonMarkup } from "./trend-comparison-view.js";
import { createOilPressureGraph } from "./oil-pressure-graph.js";
import { createTrendChartUtils } from "./trend-chart-utils.js";
import { createTrendChartScope } from "./trend-chart-scope.js";
import { createTrendTimeScatter } from "./trend-charts/time-scatter.js";
import { createOilPressureTrendChart } from "./trend-charts/oil-pressure.js";
import { createInductionLeakTrendChart } from "./trend-charts/induction-leak.js";
import { createCoolingMarginTrendChart } from "./trend-charts/cooling-margin.js";
import { createChtCoolingRateTrendChart } from "./trend-charts/cht-cooling-rate.js";
import { createEgtBalanceTrendChart } from "./trend-charts/egt-balance.js";
import { createTurboMarginTrendChart } from "./trend-charts/turbo-margin.js";
import { createEgtRhythmTrendChart } from "./trend-charts/egt-rhythm.js";
import { createElectricalTrendChart } from "./trend-charts/electrical.js";
import { createSensorQualityTrendChart } from "./trend-charts/sensor-quality.js";
import { createGamiSweepTrendChart } from "./trend-charts/gami-sweep.js";
import { createChtLanesTrendChart } from "./trend-charts/cht-lanes.js";
import { createOverallHealthPanel } from "./trend-charts/overall-health.js";
import { createChartReviewContent } from "./trend-charts/review-content.js";

const heatBands = Object.freeze([
  { key: "below-325", label: "<325 F", color: "#4f9bff", dark: "#2f6fb8" },
  { key: "325-349", label: "325-349 F", color: "#5aa7ff", dark: "#2f78c8" },
  { key: "350-374", label: "350-374 F", color: "#62cfc1", dark: "#209d8c" },
  { key: "375-399", label: "375-399 F", color: "#ffd241", dark: "#d79b13" },
  { key: "400-424", label: "400-424 F", color: "#ff933e", dark: "#df5a21" },
  { key: "425+", label: "425+ F", color: "#ff5c45", dark: "#c92220" },
]);


const egtBalanceThresholds = Object.freeze({
  balancedF: 60,
  reviewF: 90,
});

const chtCoolingThresholds = Object.freeze({
  compareFpm: 40,
  reviewFpm: 50,
});


export function trendFlightAxisIndexes(count, maxLabels) {
  const total = Math.max(0, Number(count) || 0);
  if (!total) return [];
  const labelCount = Math.max(2, Math.min(total, Number(maxLabels) || total));
  if (total <= labelCount) return Array.from({ length: total }, (_, index) => index);
  const indexes = new Set([0, total - 1]);
  for (let labelIndex = 1; labelIndex < labelCount - 1; labelIndex += 1) {
    indexes.add(Math.round(labelIndex * (total - 1) / (labelCount - 1)));
  }
  return [...indexes].sort((a, b) => a - b);
}

export function trendVisualAllowsCylinderScope({ key, activeEngine = "all", engineOptionCount = 0 } = {}) {
  if (key !== "chtHeatProfile" && key !== "chtDistribution") return false;
  return Number(engineOptionCount) < 2 || activeEngine !== "all";
}

const visualDefinitions = Object.freeze([
  {
    key: "chtHeatProfile",
    metricKey: "chtScore",
    label: "CHT Heat Profile",
    subtitle: "Cumulative operating time in each temperature range",
    question: "How much operating time did each cylinder spend in each CHT range?",
  },
  {
    key: "chtDistribution",
    metricKey: "chtScore",
    label: "CHT Distribution Lanes",
    subtitle: "Where each cylinder usually runs",
    question: "Where does each cylinder live?",
  },
  {
    key: "egtBalance",
    metricKey: "egtScore",
    label: "EGT Balance",
    subtitle: "Cylinder-to-cylinder exhaust spread over time",
    question: "Are the EGTs staying balanced across comparable flights?",
  },
  {
    key: "turboMargin",
    metricKey: "turboScore",
    label: "Turbo Margin",
    subtitle: "MAP and TIT margin by flight",
    question: "Are boost and temperature margins tightening across flights?",
  },
  {
    key: "inductionLeak",
    metricKey: "inductionLeakScore",
    label: "Induction Leak Cues",
    subtitle: "Fuel-flow drop with EGT rise across flights",
    question: "Are induction-leak cues repeating or strengthening?",
  },
  {
    key: "coolingMargin",
    metricKey: "coolingScore",
    label: "Highest CHT by Flight",
    subtitle: "Flight-by-flight high-end CHT snapshot",
    question: "Which cylinder has the highest CHT snapshot on each flight?",
  },
  {
    key: "chtCoolingRate",
    metricKey: "coolingRateScore",
    label: "CHT Cooling Rate",
    subtitle: "Fastest CHT cooldown by flight",
    question: "Are descents or power reductions cooling cylinders too quickly?",
  },
  {
    key: "oilPressure",
    metricKey: "oilSystemScore",
    label: "Oil Pressure by Flight",
    subtitle: "Typical pressure and stability by flight",
    question: "Is oil pressure stable?",
  },
  {
    key: "oilEnvelope",
    metricKey: "oilSystemScore",
    label: "Oil Pressure vs Oil Temperature",
    subtitle: "Pressure behavior as oil warms",
    question: "Does pressure settle normally as oil warms?",
  },
  {
    key: "egtRhythm",
    metricKey: "valveScore",
    label: "EGT Rhythm Signature",
    subtitle: "Cyclical EGT cue across stable windows",
    question: "Is any cylinder showing rhythm?",
  },
  {
    key: "electricalVoltage",
    metricKey: "electricalScore",
    label: "Electrical",
    subtitle: "Running bus voltage stability by flight",
    question: "Is bus voltage staying in its expected running band?",
  },
  {
    key: "sensorQuality",
    metricKey: "sensorScore",
    label: "Sensor Quality Trust Trend",
    subtitle: "Whether the trend evidence is trustworthy",
    question: "Which sensors look suspect?",
  },
  {
    key: "gamiSweep",
    metricKey: "gamiSweepScore",
    label: "GAMI Sweep Spread",
    subtitle: "Saved injector-balance sweeps",
    question: "Are saved GAMI sweeps changing?",
  },
]);

export function createTrendVisualWorkspace({
  escapeHtml,
  trendSignalDefinitionsForRows,
  buildTrendSignalSummary,
  buildTrendSignalSummaries,
  buildGamiTrendAggregate,
  overallTrendStatus,
  trendMetricReviewEnabled = () => true,
  getActiveTrendMetric,
  setActiveTrendMetric,
  // Optional frame-yield used to fill gallery chart SVGs lazily (one card per
  // frame) after the skeleton pass. When absent (tests, golden harness) every
  // chart renders inline exactly as before, so snapshot byte-parity holds.
  waitForUiFrame = null,
  renderMaintenanceContext = null,
}) {
  // Explicit view state (Phase 4b): everything the user's interactions select, in one object the
  // render functions read and the event handlers mutate. Point selections are keyed by the CHART
  // key (matching visualDefinitions / the data attributes), replacing eight per-chart closure vars.
  // Render caches (lastRows/lastAircraft, the gallery key/epoch, memo WeakMaps) deliberately stay
  // OUTSIDE view — they are derived render state, not user selections.
  const POINT_CHART_KEYS = Object.freeze([
    "egtBalance",
    "turboMargin",
    "inductionLeak",
    "egtRhythm",
    "chtCoolingRate",
    "oilPressure",
    "electricalVoltage",
    "coolingMargin",
  ]);
  const view = {
    visualKey: "chtHeatProfile",
    engine: "all",
    cylinder: "all",
    pointIndexByChart: Object.fromEntries(POINT_CHART_KEYS.map((key) => [key, null])),
    sensorQualityPointIndex: null,
  };
  let lastRows = [];
  let lastAircraft = "";
  const engineByAircraft = new Map();

  // The CHT heat profile is a shared renderer (also used by the Overview
  // detector-visual panel). Trends owns engine/cylinder scope state and passes it
  // in per render; the module is pure markup. See src/app/cht-heat-profile.js.
  const chtHeatProfile = createChtHeatProfile({ escapeHtml });
  const chtDistributionLanes = createChtDistributionLanes({ escapeHtml });
  const oilPressureGraph = createOilPressureGraph({ escapeHtml });

  // Phase 4f: shared pure helpers + engine/cylinder identity helpers, moved verbatim to
  // trend-chart-utils.js / trend-chart-scope.js. Destructured under their original names so the
  // call sites are unchanged; the view-bound and lastRows-bound variants remain as thin wrappers.
  const chartUtils = createTrendChartUtils({ escapeHtml });
  const {
    finite,
    positiveCylinderNumber,
    median,
    percentile,
    formatMinutes,
    statusClassForScore,
    shortDateLabel,
    flightAxisAnchor,
    temperatureColor,
    weightedDistributionQuantile,
    smoothPathFromPoints,
    renderEmptySvg,
    compactFlightList,
    clampedPointIndex,
  } = chartUtils;
  const chartScope = createTrendChartScope();
  const {
    cylinderDisplayLabel,
    cylinderIdentityCandidates,
    cylinderIdentityForRows,
    cylinderLabelForRows,
    engineControlLabel,
    engineOrder,
    engineOptionsForRows,
  } = chartScope;

  // Phase 4g+: per-chart modules. Each receives the shared helpers and gets the view state
  // threaded per call; the workspace keeps thin delegates so the dispatchers stay unchanged
  // until the 4j registry collapse.
  const timeScatter = createTrendTimeScatter({ escapeHtml, utils: chartUtils });
  const oilPressureChart = createOilPressureTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, timeScatter, oilPressureGraph });
  const inductionLeakChart = createInductionLeakTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope });
  const coolingMarginChart = createCoolingMarginTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, aggregateHeatBands, cylinderLabelForRow });
  const chtCoolingRateChart = createChtCoolingRateTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, chtCoolingThresholds, selectedCylinderNumbers, cylinderLabelForRow, trendFlightAxisIndexes });
  const egtBalanceChart = createEgtBalanceTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, egtBalanceThresholds, trendFlightAxisIndexes });
  const turboMarginChart = createTurboMarginTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, trendFlightAxisIndexes });
  const egtRhythmChart = createEgtRhythmTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, trendFlightAxisIndexes, cylinderLabelForRow });
  const electricalChart = createElectricalTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, trendFlightAxisIndexes });
  const sensorQualityChart = createSensorQualityTrendChart({ escapeHtml, utils: chartUtils });
  const gamiSweepChart = createGamiSweepTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope });
  const chtLanesChart = createChtLanesTrendChart({ escapeHtml, utils: chartUtils, scope: chartScope, chtHeatProfile, chtDistributionLanes, aggregateHeatBands });
  const overallHealthPanel = createOverallHealthPanel({ escapeHtml, utils: chartUtils, scope: chartScope, overallSignals, overallTrendStatus });
  const reviewContent = createChartReviewContent({ escapeHtml });

  // Phase 4e: rowsByDate is called dozens of times per render (every default-param
  // `rows = rowsByDate(lastRows)` site) and on every selection-only re-render. Memoize by input
  // array identity; the cache is dropped whenever a render arrives with dataChanged, so an in-place
  // mutation of a re-used input array can never serve a stale sort (the controller passes a fresh
  // array per data change anyway — this is belt and suspenders). A stable output identity also
  // lets the per-engine aggregate caches below key off the sorted array itself.
  let rowsByDateCache = new WeakMap();
  function rowsByDate(rows) {
    const cacheable = Array.isArray(rows);
    if (cacheable && rowsByDateCache.has(rows)) return rowsByDateCache.get(rows);
    const sorted = (rows ?? [])
      .filter((row) => row && !row.error)
      .slice()
      .sort((a, b) => (a.dateValue || 0) - (b.dateValue || 0));
    if (cacheable) rowsByDateCache.set(rows, sorted);
    return sorted;
  }

  // Per-(rows, engineId) memo for the two heavy CHT aggregations. Keyed by the rows array identity
  // (stable across selection-only re-renders thanks to the rowsByDate memo); results are treated as
  // immutable by every consumer (they copy before sorting). Dropped with the rowsByDate cache.
  let aggregateCache = new WeakMap();
  function memoizedAggregate(kind, rows, engineId, compute) {
    if (!Array.isArray(rows)) return compute();
    let byKind = aggregateCache.get(rows);
    if (!byKind) { byKind = new Map(); aggregateCache.set(rows, byKind); }
    const cacheKey = `${kind}|${engineId}`;
    if (byKind.has(cacheKey)) return byKind.get(cacheKey);
    const result = compute();
    byKind.set(cacheKey, result);
    return result;
  }

  function overallBucketLabel(bucket) {
    return overallHealthPanel.overallBucketLabel(bucket);
  }

  function overallBucketClass(bucket) {
    return overallHealthPanel.overallBucketClass(bucket);
  }

  // Building the per-signal summaries is the most expensive thing the overall
  // helpers do, and within a single render the same rows array is queried many
  // times (every graph card asks for its metric's status, plus the overall
  // panel). Memoize by the array identity so one render computes it once.
  const overallSignalsCache = new WeakMap();
  function overallSignals(rows) {
    if (overallSignalsCache.has(rows)) return overallSignalsCache.get(rows);
    const signals = typeof trendSignalDefinitionsForRows === "function" && typeof buildTrendSignalSummary === "function"
      ? trendSignalDefinitionsForRows(rows).map((definition) => buildTrendSignalSummary(rows, definition)).filter(Boolean)
      : (buildTrendSignalSummaries?.(rows) ?? []);
    const result = signals.map((signal) => {
      const stable = !signal.flaggedCount && Number.isFinite(signal.latestScore) && signal.latestScore >= 88;
      return {
        ...signal,
        status: stable ? { label: "Stable", className: "green" } : signal.status,
      };
    });
    if (rows && typeof rows === "object") overallSignalsCache.set(rows, result);
    return result;
  }

  function overallSignalBucket(signal) {
    return overallHealthPanel.overallSignalBucket(signal);
  }

  function defaultOverallBucket(rows) {
    return overallHealthPanel.defaultOverallBucket(rows);
  }

  function overallStatusForMetric(rows, metricKey) {
    const signal = overallSignals(rows).find((item) => item.key === metricKey);
    if (!signal) return { label: "Limited", className: "muted" };
    const bucket = overallSignalBucket(signal);
    return { label: overallBucketLabel(bucket), className: overallBucketClass(bucket) };
  }

  function aggregateHeatBands(rows, engineId = view.engine) {
    return memoizedAggregate("heatBands", rows, engineId, () => computeHeatBandsAggregate(rows, engineId));
  }

  function computeHeatBandsAggregate(rows, engineId) {
    const byCylinder = new Map();
    rows.forEach((row) => {
      (row.chtHeatBandsByCylinder ?? []).forEach((item) => {
        if (!Number.isFinite(item?.cylinder)) return;
        if (!itemMatchesEngine(item, row, engineId)) return;
        const existing = byCylinder.get(item.cylinder) ?? {
          cylinder: item.cylinder,
          sourceCylinder: item.sourceCylinder ?? item.cylinder,
          displayCylinder: item.displayCylinder ?? item.cylinder,
          label: item.label ?? item.shortLabel ?? `Cyl ${item.cylinder}`,
          shortLabel: item.shortLabel ?? item.label ?? `Cyl ${item.cylinder}`,
          engineId: item.engineId ?? null,
          engineLabel: item.engineLabel ?? null,
          engineShortLabel: item.engineShortLabel ?? null,
          bands: new Map(heatBands.map((band) => [band.key, { ...band, seconds: 0, minutes: 0, samples: 0 }])),
          totalSeconds: 0,
          operatingSeconds: 0,
        };
        (item.bands ?? []).forEach((band) => {
          const target = existing.bands.get(band.key);
          if (!target) return;
          target.seconds += Number(band.seconds) || 0;
          target.minutes = target.seconds / 60;
          target.samples += Number(band.samples) || 0;
        });
        existing.totalSeconds += Number(item.totalSeconds) || 0;
        existing.operatingSeconds += Number(item.operatingSeconds) || 0;
        byCylinder.set(item.cylinder, existing);
      });
    });
    return [...byCylinder.values()]
      .map((item) => {
        const operatingSeconds = Math.max(item.operatingSeconds || 0, item.totalSeconds || 0);
        const belowBand = item.bands.get("below-325");
        if (belowBand) {
          belowBand.seconds = Math.max(0, operatingSeconds - item.totalSeconds);
          belowBand.minutes = belowBand.seconds / 60;
        }
        const bands = [...item.bands.values()];
        return {
          ...item,
          operatingSeconds,
          bands,
          totalMinutes: operatingSeconds / 60,
          shownWarmMinutes: item.totalSeconds / 60,
          hotMinutes: bands
          .filter((band) => band.key === "400-424" || band.key === "425+")
          .reduce((sum, band) => sum + band.minutes, 0),
          warmMinutes: bands
          .filter((band) => band.key === "375-399" || band.key === "400-424" || band.key === "425+")
          .reduce((sum, band) => sum + band.minutes, 0),
        };
      })
      .filter((item) => item.operatingSeconds > 0 || item.bands.some((band) => band.samples > 0))
      .sort((a, b) => a.cylinder - b.cylinder);
  }

  function aggregateDistribution(rows, engineId = view.engine) {
    return memoizedAggregate("distribution", rows, engineId, () => computeDistributionAggregate(rows, engineId));
  }

  function computeDistributionAggregate(rows, engineId) {
    const byCylinder = new Map();
    rows.forEach((row) => {
      (row.chtDistributionByCylinder ?? []).forEach((item) => {
        if (!Number.isFinite(item?.cylinder)) return;
        if (!itemMatchesEngine(item, row, engineId)) return;
        const existing = byCylinder.get(item.cylinder) ?? {
          cylinder: item.cylinder,
          sourceCylinder: item.sourceCylinder ?? item.cylinder,
          displayCylinder: item.displayCylinder ?? item.cylinder,
          label: item.label ?? item.shortLabel ?? `Cyl ${item.cylinder}`,
          shortLabel: item.shortLabel ?? item.label ?? `Cyl ${item.cylinder}`,
          engineId: item.engineId ?? null,
          engineLabel: item.engineLabel ?? null,
          engineShortLabel: item.engineShortLabel ?? null,
          bins: new Map(),
          samples: 0,
        };
        (item.bins ?? []).forEach((bin) => {
          const start = Number(bin.start);
          if (!Number.isFinite(start)) return;
          const current = existing.bins.get(start) ?? { start, count: 0, seconds: 0 };
          current.count += Number(bin.count) || 0;
          current.seconds += Number(bin.seconds) || 0;
          existing.bins.set(start, current);
        });
        existing.samples += Number(item.samples) || 0;
        byCylinder.set(item.cylinder, existing);
      });
    });
    return [...byCylinder.values()]
      .map((item) => ({
        ...item,
        bins: [...item.bins.values()].sort((a, b) => a.start - b.start),
      }))
      .filter((item) => item.samples > 0)
      .sort((a, b) => a.cylinder - b.cylinder);
  }

  function selectedCylinderNumbers(items) {
    if (view.cylinder === "all") return items;
    const selected = Number(view.cylinder);
    return items.filter((item) => item.cylinder === selected);
  }

  function cylinderLabelForRow(row, sourceCylinder, options = {}) {
    return cylinderLabelForRows(row ? [row] : lastRows, sourceCylinder, options);
  }

  function visualSupportsEngineScope(key = view.visualKey) {
    return new Set([
      "chtHeatProfile",
      "chtDistribution",
      "egtBalance",
      "turboMargin",
      "inductionLeak",
      "coolingMargin",
      "chtCoolingRate",
      "oilPressure",
      "oilEnvelope",
      "egtRhythm",
      "gamiSweep",
    ]).has(key);
  }

  // CHT Heat Profile and Distribution stack one lane per cylinder. On a twin that
  // is up to 12 lanes side by side, which reads as a crowded wall. Scope these two
  // visuals to a single engine at a time (the engine toggle still switches banks)
  // so each only ever draws one engine's worth of lanes.
  function visualPrefersSingleEngine(key = view.visualKey) {
    return key === "chtHeatProfile" || key === "chtDistribution";
  }

  function syncActiveEngineForRows(rows, key = view.visualKey) {
    if (!visualSupportsEngineScope(key)) {
      return;
    }
    const options = engineOptionsForRows(rows);
    if (options.length < 2) {
      view.engine = "all";
      return;
    }
    const validIds = options.map((item) => item.id);
    if (visualPrefersSingleEngine(key)) {
      // These visuals never offer "All", so an "all"/stale selection lands on the
      // first engine instead of the (now absent) combined view.
      if (!validIds.includes(view.engine)) view.engine = validIds[0];
      return;
    }
    if (!validIds.includes(view.engine)) view.engine = "all";
  }

  function visualAllowsCylinderScope(rows, key = view.visualKey) {
    const engineOptions = engineOptionsForRows(rows);
    return trendVisualAllowsCylinderScope({
      key,
      activeEngine: view.engine,
      engineOptionCount: engineOptions.length,
    });
  }

  function syncActiveCylinderForRows(rows, key = view.visualKey) {
    if (!visualAllowsCylinderScope(rows, key)) view.cylinder = "all";
  }

  function identityMatchesEngine(identity, engineId = view.engine) {
    return chartScope.identityMatchesEngine(identity, engineId);
  }

  function identityMatchesActiveEngine(identity) {
    return identityMatchesEngine(identity, view.engine);
  }

  function sourceCylinderMatchesEngine(rows, sourceCylinder, engineId = view.engine) {
    return chartScope.sourceCylinderMatchesEngine(rows, sourceCylinder, engineId);
  }

  function sourceCylinderMatchesActiveEngine(rows, sourceCylinder) {
    return sourceCylinderMatchesEngine(rows, sourceCylinder, view.engine);
  }

  function itemMatchesEngine(item, row, engineId = view.engine) {
    return chartScope.itemMatchesEngine(item, row, engineId, lastRows);
  }

  function itemMatchesActiveEngine(item, row) {
    return itemMatchesEngine(item, row, view.engine);
  }

  function resetTrendPointSelections() {
    POINT_CHART_KEYS.forEach((key) => { view.pointIndexByChart[key] = null; });
    view.sensorQualityPointIndex = null;
  }


  function renderDistributionDetail(rows) {
    return chtLanesChart.renderDistributionDetail(rows, view);
  }

  function renderDistributionStage(rows) {
    return chtLanesChart.renderDistributionStage(rows, view);
  }

  function strongestHeatCylinder(rows) {
    return chtLanesChart.strongestHeatCylinder(rows);
  }

  function metricDirection(rows, metricKey) {
    const values = rows
      .map((row) => finite(row[metricKey]))
      .filter(Number.isFinite);
    if (values.length < 4) return { label: "Needs more flights", className: "muted" };
    const recent = values.slice(-Math.min(5, values.length));
    const previous = values.slice(Math.max(0, values.length - recent.length * 2), values.length - recent.length);
    const avg = (items) => items.reduce((sum, value) => sum + value, 0) / items.length;
    if (!previous.length) return { label: "Building baseline", className: "muted" };
    const delta = avg(recent) - avg(previous);
    if (delta <= -6) return { label: "Worse recently", className: "red" };
    if (delta >= 6) return { label: "Improving", className: "green" };
    return { label: "Steady", className: "green" };
  }

  function metricReviewEnabled(metricKey) {
    return !metricKey || trendMetricReviewEnabled(metricKey) !== false;
  }

  function visualDefinitionAvailable(definition, rows) {
    return metricReviewEnabled(definition.metricKey) && trendVisualAvailable(rows, definition.key);
  }

  function availableVisualDefinitions(rows = rowsByDate(lastRows)) {
    return visualDefinitions.filter((definition) => visualDefinitionAvailable(definition, rows));
  }

  function graphDefinition(key = view.visualKey, rows = rowsByDate(lastRows)) {
    const definitions = availableVisualDefinitions(rows);
    return definitions.find((item) => item.key === key) ?? definitions[0] ?? visualDefinitions[0];
  }

  function oilSystemIssueKind(row) {
    const text = `${row?.oilSystemChannelKey ?? ""} ${row?.oilSystemChannelLabel ?? ""} ${row?.oilSystemIssueLabel ?? ""}`.toLowerCase();
    if (text.includes("temp") || text.includes("temperature") || text.includes("operating")) return "temperature";
    if (text.includes("pressure")) return "pressure";
    return null;
  }

  function preferredOilSystemVisualKey(rows) {
    const issueRows = (rows ?? [])
      .map((row, index) => ({
        row,
        index,
        kind: oilSystemIssueKind(row),
        score: finite(row?.oilSystemScore),
        hasEvidence: Boolean(row?.oilSystemIssueLabel)
          || Number.isFinite(finite(row?.oilSystemIssueValue))
          || Number.isFinite(finite(row?.oilSystemMin))
          || Number.isFinite(finite(row?.oilSystemMax)),
      }))
      .filter((item) => item.kind && item.hasEvidence)
      .sort((a, b) => (a.score ?? 100) - (b.score ?? 100) || b.index - a.index);
    return issueRows[0]?.kind === "temperature" ? "oilEnvelope" : "oilPressure";
  }

  function activeMetricVisual(metricKey, rows = rowsByDate(lastRows)) {
    if (!metricReviewEnabled(metricKey)) return null;
    if (metricKey === "overallScore") return null;
    const definitions = availableVisualDefinitions(rows);
    if (metricKey === "oilSystemScore") {
      const preferredKey = preferredOilSystemVisualKey(rows);
      const preferred = definitions.find((item) => item.key === preferredKey);
      if (preferred) return preferred;
    }
    return definitions.find((item) => item.metricKey === metricKey) ?? null;
  }

  function trendStoryMetricKey(metricKey) {
    return {
      peakCht: "chtScore",
      maxTit: "turboScore",
      titP95: "turboScore",
      maxMap: "turboScore",
      coolingRateFpm: "coolingRateScore",
      egtSpreadP90: "egtScore",
      busRange: "electricalScore",
    }[metricKey] ?? metricKey;
  }

  function selectedMetricSignal(rows, metricKey = getActiveTrendMetric()) {
    if (!metricReviewEnabled(metricKey)) return null;
    if (typeof trendSignalDefinitionsForRows !== "function" || typeof buildTrendSignalSummary !== "function") return null;
    const signalKey = trendStoryMetricKey(metricKey);
    const definition = trendSignalDefinitionsForRows(rows).find((item) => item.key === signalKey);
    const signal = definition ? buildTrendSignalSummary(rows, definition) : null;
    if (!signal) return null;
    const stable = !signal.flaggedCount && Number.isFinite(signal.latestScore) && signal.latestScore >= 88;
    return stable ? { ...signal, status: { label: "Stable", className: "green" } } : signal;
  }

  function metricReviewRepeatText(signal) {
    if (!signal?.flaggedCount) {
      const count = Number(signal?.metricRows) || Number(signal?.recentCount) || 0;
      return count ? `Clear across ${count}` : "No scored flights";
    }
    if (signal.recentFlagged) return `${signal.recentFlagged} of last ${signal.recentCount}`;
    return `${signal.flaggedCount} earlier`;
  }

  function metricReviewDirectionText(direction) {
    if (direction === "worse") return "Worse recently";
    if (direction === "better") return "Improving recently";
    return "Holding steady";
  }

  function metricReviewTakeaway(signal) {
    if (!signal) return "Review findings are available for the selected trend area.";
    const repeat = metricReviewRepeatText(signal).toLowerCase();
    const affected = signal.affected ? ` on ${signal.affected}` : "";
    return `${signal.label}${affected}: ${repeat}; ${metricReviewDirectionText(signal.direction).toLowerCase()}.`;
  }

  function renderMetricReviewOnlyPanel(signal) {
    if (!signal) return "";
    const list = (items) => (items ?? []).map((item) => `<li>${escapeHtml(item)}</li>`).join("");
    const latestScore = Number.isFinite(signal.latestScore) ? Math.round(signal.latestScore) : null;
    const status = signal.status ?? { label: "Watch", className: "amber" };
    return `
      <section class="trend-chart-review trend-metric-review" aria-label="Review focus for ${escapeHtml(signal.label)}">
        <div class="trend-chart-review-header">
          <p class="eyebrow">Review Focus</p>
          <h3>${escapeHtml(signal.title || `${signal.label} Review`)}</h3>
        </div>
        <div class="trend-metric-review-strip">
          <article>
            <span>Status</span>
            <strong class="status-${escapeHtml(status.className)}">${escapeHtml(status.label)}</strong>
            <small>${escapeHtml(metricReviewDirectionText(signal.direction))}</small>
          </article>
          <article>
            <span>Flights</span>
            <strong>${escapeHtml(metricReviewRepeatText(signal))}</strong>
            <small>${escapeHtml(String(signal.metricRows ?? signal.recentCount ?? 0))} scored</small>
          </article>
          <article>
            <span>Latest score</span>
            <strong>${escapeHtml(latestScore == null ? "--" : String(latestScore))}</strong>
            <small>${signal.affected ? escapeHtml(signal.affected) : "No repeated focus"}</small>
          </article>
        </div>
        <div class="trend-explanation trend-chart-review-grid">
          <div>
            <span>What this means</span>
            <p>${escapeHtml(signal.meaning || signal.action || "This trend area has findings worth reviewing against the source flight data.")}</p>
          </div>
          <div>
            <span>Why it matters</span>
            <p>${escapeHtml(signal.why || "Repeated findings are most useful when they are compared against phase, operating context, and sensor trust.")}</p>
          </div>
          <div>
            <span>Common explanations</span>
            <ul>${list(signal.contexts)}</ul>
          </div>
          <div>
            <span>What to check</span>
            <ul>${list(signal.checks)}</ul>
          </div>
        </div>
      </section>
    `;
  }

  function syncVisualToMetric(rows = rowsByDate(lastRows)) {
    let visualReset = false;
    if (!availableVisualDefinitions(rows).some((definition) => definition.key === view.visualKey)) {
      view.visualKey = graphDefinition(view.visualKey, rows).key;
      view.cylinder = "all";
      resetTrendPointSelections();
      visualReset = true;
    }
    const activeMetric = getActiveTrendMetric();
    if (activeMetric !== "overallScore" && !metricReviewEnabled(activeMetric)) {
      const definition = graphDefinition(view.visualKey, rows);
      if (definition?.metricKey && metricReviewEnabled(definition.metricKey)) setActiveTrendMetric(definition.metricKey);
    }
    const metricVisual = activeMetricVisual(getActiveTrendMetric(), rows);
    if (metricVisual && (visualReset || graphDefinition(view.visualKey, rows).metricKey !== metricVisual.metricKey)) {
      view.visualKey = metricVisual.key;
      view.cylinder = "all";
      resetTrendPointSelections();
    }
    syncActiveEngineForRows(rows);
    syncActiveCylinderForRows(rows);
  }

  function overallMixTakeaway(rows) {
    return overallHealthPanel.overallMixTakeaway(rows);
  }

  function renderOverallHealthDetail(rows) {
    return overallHealthPanel.renderOverallHealthDetail(rows);
  }

  function renderOverallHealthPanel(rows) {
    return overallHealthPanel.renderOverallHealthPanel(rows, view);
  }

  function renderChartReviewContent(key) {
    return `<details class="trend-chart-explanation"><summary>How to read this chart</summary>${reviewContent.renderChartReviewContent(key)}</details>`;
  }

  function heatProfileTakeaway(rows) {
    return chtLanesChart.heatProfileTakeaway(rows);
  }

  function renderHeatProfileDetail(rows) {
    return chtLanesChart.renderHeatProfileDetail(rows, view);
  }

  function coolingMarginPoints(rows, options = {}) {
    return coolingMarginChart.coolingMarginPoints(rows, options, view);
  }

  function coolingDominantCylinder(points) {
    return coolingMarginChart.coolingDominantCylinder(points);
  }

  function renderCoolingMarginDetail(rows) {
    return coolingMarginChart.renderCoolingMarginDetail(rows, view);
  }

  function renderCoolingMarginStage(rows) {
    return coolingMarginChart.renderCoolingMarginStage(rows, view);
  }

  function chtCoolingPoints(rows) {
    return chtCoolingRateChart.chtCoolingPoints(rows, view);
  }

  function renderChtCoolingDetail(rows) {
    return chtCoolingRateChart.renderChtCoolingDetail(rows, view);
  }

  function renderHeatProfile(rows, options = {}) {
    return chtLanesChart.renderHeatProfile(rows, options, view);
  }

  function renderDistribution(rows, options = {}) {
    return chtLanesChart.renderDistribution(rows, options, view);
  }

  function renderCoolingMargin(rows, options = {}) {
    return coolingMarginChart.renderCoolingMargin(rows, options, view);
  }

  function renderChtCoolingRate(rows, options = {}) {
    return chtCoolingRateChart.renderChtCoolingRate(rows, options, view);
  }

  function renderOilPressure(rows, options = {}) {
    return oilPressureChart.render(rows, options, view);
  }

  function renderOilEnvelope(rows, options = {}) {
    return oilPressureChart.renderEnvelope(rows, options, view);
  }

  function egtBalancePoints(rows, options = {}) {
    return egtBalanceChart.egtBalancePoints(rows, options, view);
  }

  function renderEgtBalanceDetail(rows) {
    return egtBalanceChart.renderEgtBalanceDetail(rows, view);
  }

  function renderEgtBalance(rows, options = {}) {
    return egtBalanceChart.renderEgtBalance(rows, options, view);
  }

  function turboPoints(rows, options = {}) {
    return turboMarginChart.turboPoints(rows, options, view);
  }

  function turboPointStatus(point) {
    return turboMarginChart.turboPointStatus(point);
  }

  function turboStatusLabel(point) {
    return turboMarginChart.turboStatusLabel(point);
  }

  function renderTurboDetail(rows) {
    return turboMarginChart.renderTurboDetail(rows, view);
  }

  function renderTurboMargin(rows, options = {}) {
    return turboMarginChart.renderTurboMargin(rows, options, view);
  }

  function inductionLeakPoints(rows) {
    return inductionLeakChart.points(rows, view);
  }

  function inductionLeakSeverity(point) {
    return inductionLeakChart.severity(point);
  }

  function inductionLeakRepeatedCylinder(points) {
    return inductionLeakChart.repeatedCylinder(points);
  }

  function renderInductionLeakDetail(rows) {
    return inductionLeakChart.renderDetail(rows, view);
  }

  function renderInductionLeak(rows, options = {}) {
    return inductionLeakChart.render(rows, options, view);
  }

  function egtRhythmPoints(rows, options = {}) {
    return egtRhythmChart.egtRhythmPoints(rows, options, view);
  }

  function renderEgtRhythm(rows, options = {}) {
    return egtRhythmChart.renderEgtRhythm(rows, options, view);
  }

  function sensorQualityPoints(rows) {
    return sensorQualityChart.sensorQualityPoints(rows);
  }

  function renderSensorQualityDetail(rows) {
    return sensorQualityChart.renderSensorQualityDetail(rows, view);
  }

  function renderSensorQuality(rows, options = {}) {
    return sensorQualityChart.renderSensorQuality(rows, options, view);
  }

  function electricalPoints(rows) {
    return electricalChart.electricalPoints(rows);
  }

  function electricalSystemBand(points) {
    return electricalChart.electricalSystemBand(points);
  }

  function renderElectricalDetail(rows) {
    return electricalChart.renderElectricalDetail(rows, view);
  }

  function renderElectricalVoltage(rows, options = {}) {
    return electricalChart.renderElectricalVoltage(rows, options, view);
  }

  function renderGamiSweep(rows, options = {}) {
    return gamiSweepChart.renderGamiSweep(rows, options, view);
  }

  function renderChartByKey(key, rows, options = {}) {
    const spec = chartSpecs[key] ?? chartSpecs.chtHeatProfile;
    return spec.render(rows, options);
  }

  // Phase 4j: ONE spec registry replaces the four per-key dispatch chains (renderChartByKey's
  // if/else, the detail-panel ternary, chartTakeaway's chain, and the stage/class special-cases).
  // Each visual key maps to { render, renderDetail?, renderStage?, stageClass?, takeaway }.
  const chartSpecs = Object.freeze({
    chtHeatProfile: {
      render: (rows, options) => renderHeatProfile(rows, options),
      renderDetail: (rows) => renderHeatProfileDetail(rows),
      takeaway: (rows) => heatProfileTakeaway(rows),
    },
    chtDistribution: {
      render: (rows, options) => renderDistribution(rows, options),
      renderDetail: (rows) => renderDistributionDetail(rows),
      renderStage: (rows) => renderDistributionStage(rows),
      stageClass: "is-distribution-detail",
      takeaway: (rows) => {
      const strongest = aggregateDistribution(rows)
        .map((item) => ({ ...item, median: weightedDistributionQuantile(item.bins, 0.5) }))
        .filter((item) => Number.isFinite(item.median))
        .sort((a, b) => b.median - a.median)[0];
      return strongest ? `${cylinderDisplayLabel(strongest)} has the highest median CHT across the selected history.` : "CHT distribution needs more warm operating samples.";
      },
    },
    egtBalance: {
      render: (rows, options) => renderEgtBalance(rows, options),
      renderDetail: (rows) => renderEgtBalanceDetail(rows),
      takeaway: (rows) => {
      const points = egtBalancePoints(rows);
      if (!points.length) return "EGT balance needs spread data from comparable engine-run samples.";
      const reviewRows = points.filter((point) => point.spread >= egtBalanceThresholds.reviewF);
      const latest = points.at(-1);
      if (reviewRows.length) return `${reviewRows.length} flight${reviewRows.length === 1 ? "" : "s"} reached the EGT spread review band; compare phase, mixture, fuel flow, and sensor trust.`;
      if (latest?.spread >= egtBalanceThresholds.balancedF) return `Latest EGT spread is ${Math.round(latest.spread)} F, worth comparing against similar steady-power operation.`;
      return `Latest EGT spread is ${Math.round(latest.spread)} F, inside the balanced band for this trend view.`;
      },
    },
    turboMargin: {
      render: (rows, options) => renderTurboMargin(rows, options),
      renderDetail: (rows) => renderTurboDetail(rows),
      takeaway: (rows) => {
      const points = turboPoints(rows);
      if (!points.length) return "Turbo margin needs MAP, TIT, upper-deck pressure, or configured turbo profile context.";
      const missingLimits = points.filter((point) => point.status === "needs-profile-limits").length;
      const reviewRows = points.filter((point) => turboPointStatus(point) === "red");
      const watchRows = points.filter((point) => turboPointStatus(point) === "amber");
      const latest = points.at(-1);
      if (reviewRows.length) return `${reviewRows.length} turbo review flight${reviewRows.length === 1 ? "" : "s"} found; compare MAP, TIT, fuel flow, RPM, altitude, OAT, and phase before drawing conclusions.`;
      if (watchRows.length) return `${watchRows.length} turbo watch flight${watchRows.length === 1 ? "" : "s"} approached a configured margin; inspect comparable high-power operation.`;
      if (missingLimits) return `${missingLimits} flight${missingLimits === 1 ? "" : "s"} have turbo data but need profile limits before margin can be judged.`;
      return `Latest turbo status is ${turboStatusLabel(latest).toLowerCase()}; MAP/TIT context stayed inside configured margins.`;
      },
    },
    inductionLeak: {
      render: (rows, options) => renderInductionLeak(rows, options),
      renderDetail: (rows) => renderInductionLeakDetail(rows),
      takeaway: (rows) => {
      const points = inductionLeakPoints(rows);
      const events = points.filter((point) => point.hasEvent);
      if (!points.length) return "Not enough induction-leak trend data to compare cues across the selected history yet.";
      if (!events.length) return "No induction-leak cue is marked in this selected history.";
      const repeated = inductionLeakRepeatedCylinder(events);
      const strongest = events.slice().sort((a, b) => inductionLeakSeverity(b) - inductionLeakSeverity(a))[0] ?? null;
      if (repeated && repeated[1] > 1) return `Cyl ${Math.round(repeated[0])} repeats across ${repeated[1]} induction-leak cue flights; inspect the sample windows for fuel-flow, EGT, and MAP agreement.`;
      return `${events.length} induction-leak cue flight${events.length === 1 ? "" : "s"} found${strongest?.egtRise ? `; strongest EGT rise is ${Math.round(strongest.egtRise)} F` : ""}.`;
      },
    },
    coolingMargin: {
      render: (rows, options) => renderCoolingMargin(rows, options),
      renderDetail: (rows) => renderCoolingMarginDetail(rows),
      renderStage: (rows) => renderCoolingMarginStage(rows),
      stageClass: "is-cooling-detail",
      takeaway: (rows) => {
      const points = coolingMarginPoints(rows);
      const dominant = coolingDominantCylinder(points);
      const reviewRows = points.filter((point) => point.p95 >= 420);
      if (dominant) return `${cylinderLabelForRows(rows, dominant[0])} had the highest high-end CHT on ${dominant[1]} flight${dominant[1] === 1 ? "" : "s"}; Heat Profile still shows total warm exposure.`;
      return reviewRows.length ? `${reviewRows.length} flight${reviewRows.length === 1 ? "" : "s"} reached the review band.` : "High-end CHT snapshots stayed out of the review band in the trend rows.";
      },
    },
    chtCoolingRate: {
      render: (rows, options) => renderChtCoolingRate(rows, options),
      renderDetail: (rows) => renderChtCoolingDetail(rows),
      takeaway: (rows) => {
      const points = chtCoolingPoints(rows);
      if (!points.length) return "CHT cooling needs rapid-cooling event data from analyzed flights.";
      const reviewRows = points.filter((point) => point.rateFpm >= chtCoolingThresholds.reviewFpm);
      const latest = points.at(-1);
      if (!Number.isFinite(latest?.rateFpm)) return "No measured rapid-cooling event is available for the latest flight.";
      if (reviewRows.length) return `${reviewRows.length} flight${reviewRows.length === 1 ? "" : "s"} reached the rapid-cooling review line; compare descent profile, power reduction, airspeed, and sensor trust.`;
      if (latest?.rateFpm >= chtCoolingThresholds.compareFpm) return `Latest CHT cooling rate is ${Math.round(latest.rateFpm)} F/min, worth comparing against similar descents.`;
      return `Latest CHT cooling rate is ${Math.round(latest.rateFpm)} F/min, below the rapid-cooling compare line.`;
      },
    },
    oilPressure: {
      render: (rows, options) => renderOilPressure(rows, options),
      takeaway: (rows) => {
        const direction = metricDirection(rows, "oilSystemScore");
        return `Oil-system score: ${direction.label.toLowerCase()}. Compare pressure with oil temperature and RPM.`;
      },
    },
    oilEnvelope: {
      render: (rows, options) => renderOilEnvelope(rows, options),
      stageClass: "is-oil-detail",
      takeaway: () => "The envelope shows whether oil pressure settles in the expected shape as oil temperature rises.",
    },
    egtRhythm: {
      render: (rows, options) => renderEgtRhythm(rows, options),
      takeaway: (rows) => {
      const rhythmRows = rows.filter((row) => Number.isFinite(row.valveAmplitude) && row.valveScore < 88);
      if (!rhythmRows.length) return "No concerning repeated EGT rhythm cue is visible in this history.";
      const flights = compactFlightList(rhythmRows);
      return `${rhythmRows.length} flight${rhythmRows.length === 1 ? "" : "s"} showed a rhythm cue worth review.${flights ? ` Flights: ${flights}.` : ""}`;
      },
    },
    electricalVoltage: {
      render: (rows, options) => renderElectricalVoltage(rows, options),
      renderDetail: (rows) => renderElectricalDetail(rows),
      takeaway: (rows) => {
      const points = electricalPoints(rows);
      if (!points.length) return "Electrical trend needs bus voltage data before it can compare voltage stability.";
      const band = electricalSystemBand(points);
      const outOfBand = points.filter((point) => (
        Number.isFinite(point.minBus) && point.minBus < band.low
        || Number.isFinite(point.maxBus) && point.maxBus > band.high
        || Number.isFinite(point.outRatio) && point.outRatio > 0
      ));
      const widest = points.slice().sort((a, b) => (b.range ?? -1) - (a.range ?? -1))[0];
      if (outOfBand.length) return `${outOfBand.length} flight${outOfBand.length === 1 ? "" : "s"} had running bus voltage outside the expected band; compare load, RPM, and electrical work history.`;
      return `Running bus stayed in band; widest flight range is ${Number.isFinite(widest?.range) ? widest.range.toFixed(1) : "--"} V.`;
      },
    },
    sensorQuality: {
      render: (rows, options) => renderSensorQuality(rows, options),
      renderDetail: (rows) => renderSensorQualityDetail(rows),
      takeaway: (rows) => sensorQualityChart.sensorQualityTakeaway(rows),
    },
    gamiSweep: {
      render: (rows, options) => renderGamiSweep(rows, options),
      takeaway: () => "Saved GAMI sweeps compare injector-balance results only when the sweep was intentionally marked.",
    },
  });

  function chartTakeaway(key, rows) {
    const spec = chartSpecs[key];
    if (spec?.takeaway) return spec.takeaway(rows);
    return "Use the graph to compare repeated behavior before treating one flight as a trend.";
  }

  function renderCylinderControls() {
    const rows = rowsByDate(lastRows);
    const supportsCylinder = visualAllowsCylinderScope(rows);
    const cylinders = (view.visualKey === "chtDistribution" ? aggregateDistribution(rows) : aggregateHeatBands(rows))
      .map((item) => ({ value: String(item.cylinder), label: cylinderDisplayLabel(item) }));
    const values = [{ value: "all", label: "All" }, ...cylinders];
    return `
      <div class="trend-cylinder-controls" ${supportsCylinder ? "" : "hidden"}>
        ${values.map((item) => `
          <button type="button" data-trend-cylinder="${escapeHtml(item.value)}" class="${view.cylinder === item.value ? "is-active" : ""}" aria-pressed="${view.cylinder === item.value ? "true" : "false"}">${escapeHtml(item.label)}</button>
        `).join("")}
      </div>
    `;
  }

  function renderTrendPictureControls(rows, key = view.visualKey) {
    // Phase 5e: engine scope moved out of the per-chart controls into the page-level
    // #trendScopeBar (renderEngineScopeBar); only the cylinder picker stays with the chart.
    return renderCylinderControls();
  }

  // Page-level engine scope (Phase 5e): one sticky control under the hero drives view.engine for
  // the charts. Engine-agnostic charts retain the selection for the next chart.
  // Unlike the old per-chart control it
  // remains visible while the Overall panel is focused (the gallery is engine-scoped, so switching
  // banks re-scopes the thumbnails). Lane visuals draw a single engine, so they drop "All".
  function renderEngineScopeBar(rows) {
    if (!visualSupportsEngineScope(view.visualKey)) return "";
    const options = engineOptionsForRows(rows);
    if (options.length <= 1) return "";
    const values = visualPrefersSingleEngine(view.visualKey) ? options : [{ id: "all", label: "All" }, ...options];
    return `
      <div class="trend-engine-controls" role="group" aria-label="Engine scope for trend charts">
        <span class="trend-scope-bar-label">Engine</span>
        ${values.map((item) => `
          <button type="button" data-trend-engine="${escapeHtml(item.id)}" class="${view.engine === item.id ? "is-active" : ""}" aria-pressed="${view.engine === item.id ? "true" : "false"}">${escapeHtml(item.label)}</button>
        `).join("")}
      </div>
      <small class="trend-engine-scope-note">Engine selection scopes charts. Scores and comparison evidence cover the selected aircraft history.</small>
    `;
  }

  // With waitForUiFrame injected, a full gallery rebuild becomes a two-phase
  // render: the skeleton pass writes every card's button/label/status plus the
  // ACTIVE card's chart inline (so the thumbnail the user just selected never
  // blanks), and fillPendingGalleryCharts() then renders the remaining chart
  // SVGs one per frame. The card markup is identical between modes — only the
  // chart span starts empty — and the fixed 108px .trend-graph-card-chart
  // height means skeletons hold layout exactly (no shift when charts land).
  const lazyGalleryFill = typeof waitForUiFrame === "function";

  function renderGraphCards(rows, { pendingKeys = null, activeKey = view.visualKey } = {}) {
    return availableVisualDefinitions(rows).map((definition) => {
      const direction = overallStatusForMetric(rows, definition.metricKey);
      const active = definition.key === activeKey;
      const deferChart = pendingKeys !== null && !active;
      if (deferChart) pendingKeys.push(definition.key);
      return `
        <button class="trend-graph-card${active ? " is-active" : ""}" type="button" data-trend-visual="${escapeHtml(definition.key)}" aria-pressed="${active ? "true" : "false"}">
          <span class="trend-graph-card-top">
            <strong>${escapeHtml(definition.label)}</strong>
            <em class="status-${escapeHtml(direction.className)}">${escapeHtml(direction.label)}</em>
          </span>
          <small>${escapeHtml(definition.subtitle)}</small>
          <span class="trend-graph-card-chart">${deferChart ? "" : renderChartByKey(definition.key, rows, { compact: true })}</span>
        </button>
      `;
    }).join("");
  }

  // Generation token for the lazy fill: every rebuild starts a new fill run and
  // orphans the previous one (compare after each await — the prewarm-token
  // pattern from trend-sample-graphs).
  let galleryFillToken = 0;

  async function fillPendingGalleryCharts(container, rows, pendingKeys) {
    const token = ++galleryFillToken;
    for (const key of pendingKeys) {
      await waitForUiFrame();
      if (token !== galleryFillToken) return;
      const span = container.querySelector(`[data-trend-visual="${key}"] .trend-graph-card-chart`);
      if (!span) continue;
      span.innerHTML = renderChartByKey(key, rows, { compact: true });
    }
  }

  // The graph cards are an expensive gallery: each rebuild lays out every
  // available mini-chart SVG plus a per-card status. Their content depends only
  // on the trend data and the cylinder/point view-state shared with the big
  // chart -- never on which metric tile is "active" (that is just a highlight).
  // So we rebuild the gallery only when that data/view-state changes and
  // otherwise restyle the active card in place, which keeps metric-tile clicks
  // from re-rendering ~12 charts every time.
  let graphCardsBaseKey = null;
  let graphCardsDataEpoch = 0;
  let graphCardsRenderedPoints = {};

  // Phase 4d: compact card markup is point-selection-INVARIANT for every chart except egtRhythm,
  // whose wave shape derives from the selected point (both facts are golden-asserted). So point
  // selections no longer participate in the gallery rebuild key at all: a point click re-renders
  // ZERO cards, except a rhythm selection change, which re-renders exactly that one card's chart
  // span in place. Becomes a per-chart-module `compactReflectsSelection` flag in Phase 4h.
  const COMPACT_REFLECTS_SELECTION = Object.freeze({ egtRhythm: true });

  function syncActiveGraphCard(container, activeKey = view.visualKey) {
    container.querySelectorAll("[data-trend-visual]").forEach((button) => {
      const isActive = button.getAttribute("data-trend-visual") === activeKey;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  function scrollTrendPictureAfterRender() {
    const run = () => {
      const target = document.querySelector(".trend-picture-panel") ?? document.querySelector("#trendPictureTitle");
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    };
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }

  function renderGraphCardsInto(container, rows, { activeKey = view.visualKey } = {}) {
    // Only the data epoch and the engine/cylinder scope change what the gallery's cards contain.
    const baseKey = `${graphCardsDataEpoch}|${view.engine}|${view.cylinder}`;
    if (baseKey !== graphCardsBaseKey || !container.children.length) {
      const pendingKeys = lazyGalleryFill ? [] : null;
      container.innerHTML = renderGraphCards(rows, { pendingKeys, activeKey });
      graphCardsBaseKey = baseKey;
      graphCardsRenderedPoints = { ...view.pointIndexByChart };
      if (pendingKeys?.length) void fillPendingGalleryCharts(container, rows, pendingKeys);
    } else {
      for (const key of POINT_CHART_KEYS) {
        if (view.pointIndexByChart[key] === graphCardsRenderedPoints[key]) continue;
        graphCardsRenderedPoints[key] = view.pointIndexByChart[key];
        if (!COMPACT_REFLECTS_SELECTION[key]) continue; // compact markup is selection-invariant
        const span = container.querySelector(`[data-trend-visual="${key}"] .trend-graph-card-chart`);
        if (span) span.innerHTML = renderChartByKey(key, rows, { compact: true });
      }
    }
    syncActiveGraphCard(container, activeKey);
  }

  function renderTrendPicture(rows) {
    const activeMetric = getActiveTrendMetric();
    const overallActive = activeMetric === "overallScore";
    const activeVisual = activeMetricVisual(activeMetric, rows);
    const reviewOnlySignal = !overallActive && !activeVisual ? selectedMetricSignal(rows, activeMetric) : null;
    const reviewOnlyActive = Boolean(reviewOnlySignal);
    const definition = graphDefinition(view.visualKey, rows);
    const title = document.querySelector("#trendPictureTitle");
    const subtitle = document.querySelector("#trendPictureSubtitle");
    const takeaway = document.querySelector("#trendPictureTakeaway");
    const detail = document.querySelector("#trendPictureDetail");
    const chart = document.querySelector("#trendPictureChart");
    const controls = document.querySelector("#trendPictureControls");
    const cards = document.querySelector("#trendGraphCards");
    if (title) title.textContent = overallActive
      ? "Overall Trend Health"
      : reviewOnlyActive
        ? `${reviewOnlySignal.label} Review`
        : definition.label;
    const takeawayText = overallActive
      ? overallMixTakeaway(rows)
      : reviewOnlyActive
        ? metricReviewTakeaway(reviewOnlySignal)
        : chartTakeaway(definition.key, rows);
    if (subtitle) {
      // De-chroming: the static subtitle narrates the question the data-driven
      // takeaway line answers, so it renders only when no takeaway is available.
      subtitle.textContent = takeawayText
        ? ""
        : overallActive
          ? "How are the tracked areas split across stable, watch, profile, and alert states?"
          : reviewOnlyActive
            ? reviewOnlySignal.action || reviewOnlySignal.title || "Review the selected trend findings against the source flight data."
          : definition.question;
    }
    if (takeaway) takeaway.textContent = takeawayText;
    if (controls) controls.innerHTML = overallActive || reviewOnlyActive ? "" : renderTrendPictureControls(rows, definition.key);
    const scopeBar = document.querySelector("#trendScopeBar");
    if (scopeBar) {
      const scopeHtml = reviewOnlyActive ? "" : renderEngineScopeBar(rows);
      scopeBar.innerHTML = scopeHtml;
      scopeBar.hidden = !scopeHtml;
    }
    if (detail) {
      detail.innerHTML = reviewOnlyActive
        ? ""
        : overallActive
          ? renderOverallHealthDetail(rows)
          : chartSpecs[definition.key]?.renderDetail?.(rows) ?? "";
    }
    if (chart) {
      const spec = chartSpecs[definition.key] ?? {};
      chart.classList.toggle("is-overall-detail", overallActive);
      // The stage row keeps a 300px chart floor except on the Overall view, where the
      // status-mix card should hug its content (guarded: the golden harness's fake
      // region elements do not implement closest()).
      if (typeof chart.closest === "function") {
        chart.closest(".trend-picture-panel")?.classList.toggle("is-overall-view", overallActive);
      }
      ["is-distribution-detail", "is-oil-detail", "is-cooling-detail"].forEach((cls) => {
        chart.classList.toggle(cls, !overallActive && !reviewOnlyActive && spec.stageClass === cls);
      });
      chart.classList.toggle("has-chart-review", !overallActive);
      chart.classList.toggle("is-review-only", reviewOnlyActive);
      const chartHtml = overallActive
        ? renderOverallHealthPanel(rows)
        : reviewOnlyActive
          ? renderMetricReviewOnlyPanel(reviewOnlySignal)
          : spec.renderStage
          ? spec.renderStage(rows)
          : renderChartByKey(definition.key, rows);
      const comparison = overallActive ? "" : trendComparisonMarkup(selectedMetricSignal(rows, activeMetric), rows);
      const contextSlot = renderMaintenanceContext ? '<div data-trend-maintenance-context hidden></div>' : "";
      chart.innerHTML = (overallActive || reviewOnlyActive ? `${chartHtml}${comparison}` : `${chartHtml}${comparison}${renderChartReviewContent(definition.key)}`) + contextSlot;
      if (renderMaintenanceContext) {
        const identity = !overallActive && !reviewOnlyActive && view.cylinder !== "all" ? cylinderIdentityForRows(rows, view.cylinder) : null;
        renderMaintenanceContext(chart.querySelector("[data-trend-maintenance-context]"), {
          aircraft: lastAircraft,
          metricKey: trendStoryMetricKey(activeMetric),
          engineId: overallActive || reviewOnlyActive || !visualSupportsEngineScope(definition.key) ? "all" : identity?.engineId ?? view.engine,
          cylinder: identity?.displayCylinder ?? null,
          anchors: maintenanceContextTrendAnchors(rows, overallActive ? null : selectedMetricSignal(rows, activeMetric)?.evidence),
        });
      }
    }
    if (cards) renderGraphCardsInto(cards, rows, { activeKey: reviewOnlyActive ? null : view.visualKey });
  }

  function renderTrendVisualWorkspace(allRows, aircraft, options = {}) {
    lastRows = allRows ?? [];
    if ((aircraft ?? "") !== lastAircraft) {
      if (lastAircraft) engineByAircraft.set(lastAircraft, view.engine);
      view.engine = engineByAircraft.get(aircraft) ?? "all";
      if (!engineByAircraft.has(aircraft)) {
        try { view.engine = localStorage.getItem(`alaryiq.trendEngine:${aircraft}`) || "all"; } catch {}
      }
      view.cylinder = "all";
      resetTrendPointSelections();
    }
    lastAircraft = aircraft ?? "";
    // A new data epoch invalidates the cached graph-card gallery. Selection-only
    // re-renders (metric tile, graph card, point/cylinder picks) keep the epoch
    // and reuse the gallery.
    if (options.dataChanged) {
      graphCardsDataEpoch += 1;
      // Phase 4e: new data invalidates the identity-keyed sort + aggregate memos wholesale.
      rowsByDateCache = new WeakMap();
      aggregateCache = new WeakMap();
    }
    const rows = rowsByDate(lastRows);
    syncVisualToMetric(rows);
    renderTrendPicture(rows);
  }

  function setupTrendVisualWorkspace() {
    // Phase 4c: the two listener if/else chains became ordered ACTION TABLES. Each action is
    // { match(event) -> element|null, run(element) -> false to skip the re-render }. ORDER IS
    // BEHAVIOR: the arrays reproduce the historical chain precedence exactly — note the
    // coolingMargin click action sits BEFORE the graph-card action (where its branch always was),
    // and the keydown table is the six keyboard point charts then coolingMargin.
    const pointAction = ({ attr, chartKey }) => ({
      match: (event) => event.target.closest(`[${attr}]`),
      run: (element) => { view.pointIndexByChart[chartKey] = Number(element.getAttribute(attr)); },
    });
    const POINT_ACTION_ENTRIES = Object.freeze([
      { attr: "data-egt-balance-point-index", chartKey: "egtBalance", keyboard: true },
      { attr: "data-turbo-point-index", chartKey: "turboMargin", keyboard: true },
      { attr: "data-induction-leak-point-index", chartKey: "inductionLeak", keyboard: true },
      { attr: "data-egt-rhythm-point-index", chartKey: "egtRhythm", keyboard: true },
      { attr: "data-cht-cooling-point-index", chartKey: "chtCoolingRate", keyboard: true },
      { attr: "data-oil-pressure-point-index", chartKey: "oilPressure", keyboard: true },
      { attr: "data-electrical-point-index", chartKey: "electricalVoltage", keyboard: true },
    ]);
    const coolingPointEntry = Object.freeze({ attr: "data-cooling-point-index", chartKey: "coolingMargin", keyboard: true });
    const sensorQualityPointAction = {
      match: (event) => event.target.closest("[data-sensor-quality-point-index]"),
      run: (element) => { view.sensorQualityPointIndex = Number(element.getAttribute("data-sensor-quality-point-index")); },
    };

    // The overall panel's bucket-detail listing (and its data-overall-status-bucket /
    // data-overall-signal-key buttons) was removed in the alarm redesign — the status
    // mix is informational; navigation to a signal happens through the rail tiles.
    const graphCardAction = {
      match: (event) => event.target.closest("[data-trend-visual]"),
      run: (element) => {
        const shouldScrollToTrendPicture = typeof element.closest === "function" && Boolean(element.closest("#trendAiSummaryCard"));
        const key = element.getAttribute("data-trend-visual") || view.visualKey;
        view.visualKey = key;
        const definition = graphDefinition(key, rowsByDate(lastRows));
        if (definition.metricKey) setActiveTrendMetric(definition.metricKey);
        view.cylinder = "all";
        resetTrendPointSelections();
        if (shouldScrollToTrendPicture) scrollTrendPictureAfterRender();
      },
    };
    const cylinderAction = {
      match: (event) => event.target.closest("[data-trend-cylinder]"),
      run: (element) => { view.cylinder = element.getAttribute("data-trend-cylinder") || "all"; },
    };
    const engineAction = {
      match: (event) => event.target.closest("[data-trend-engine]"),
      run: (element) => {
        view.engine = element.getAttribute("data-trend-engine") || "all";
        engineByAircraft.set(lastAircraft, view.engine);
        try { localStorage.setItem(`alaryiq.trendEngine:${lastAircraft}`, view.engine); } catch {}
        view.cylinder = "all";
        resetTrendPointSelections();
      },
    };
    const clickActions = [
      pointAction(coolingPointEntry),
      graphCardAction,
      ...POINT_ACTION_ENTRIES.map(pointAction),
      sensorQualityPointAction,
      cylinderAction,
      engineAction,
    ];
    const keydownActions = [
      ...POINT_ACTION_ENTRIES.filter((entry) => entry.keyboard).map(pointAction),
      pointAction(coolingPointEntry),
      sensorQualityPointAction,
    ];

    function runFirstMatch(actions, event, { preventDefault = false } = {}) {
      for (const action of actions) {
        const element = action.match(event);
        if (!element) continue;
        if (preventDefault) event.preventDefault();
        if (action.run(element) !== false) renderTrendVisualWorkspace(lastRows, lastAircraft);
        return;
      }
    }

    document.querySelector("#view-trends")?.addEventListener("click", (event) => {
      runFirstMatch(clickActions, event);
    });
    document.querySelector("#view-trends")?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      runFirstMatch(keydownActions, event, { preventDefault: true });
    });
  }

  return {
    renderTrendVisualWorkspace,
    setupTrendVisualWorkspace,
  };
}
